import BackHeader from "@components/header/BackHeader";
import { SignupForm, VerifyPassword } from "./_components";
import styled from "@emotion/styled";
import SuccessMessage from "@components/success/SuccessMessage";
import useJoinStepStore from "@store/join/useJoinStepStore";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { joinSchema } from "@libs/zod/zodVaildation";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { SIGN_UP_URL } from "@libs/apiUrl";
import { useEffect } from "react";

export interface IFormInput {
  login_id: string | null;
  password: string | null;
  passwordConfirm: string | null;
  name: string;
  nickname: string;
  country: string;
  address: string;
  address_detail: string;
  postcode: string;
  gender: string;
  year: string;
  month: string;
  day: string;
  profile_image: string | null;
  isNicknameUnique: boolean;
  oauth_provider: string | null;
  oauth_id: string | null;
  country_name: string;
}

export default function JoinPage() {
  const { step, setStep, decreaseStep } = useJoinStepStore();
  const location = useLocation();

  // null아니면 true 소셜로그인 // null이면 false 일반로그인
  const isSocialLogin =
    location.state?.login_type !== "null" &&
    location.state?.login_type !== undefined;

  const socialUserData = location.state?.userData;

  const navigate = useNavigate();
  const methods = useForm<IFormInput>({
    resolver: zodResolver(joinSchema(isSocialLogin)),
    mode: "onChange",
    reValidateMode: "onChange",
    shouldUnregister: false,

    defaultValues: {
      login_id: isSocialLogin
        ? socialUserData?.oauth_provider === "APPLE"
          ? "" // 애플 로그인이면 빈 문자열(나중에 useEffect에서 설정)
          : socialUserData?.email || null // 다른 소셜 로그인이면 email 또는 null
        : "", // 일반 로그인이면 빈 문자열
      password: isSocialLogin ? null : "",
      passwordConfirm: isSocialLogin ? null : "",
      profile_image: null,
      name: isSocialLogin && socialUserData ? socialUserData.name || "" : "",
      nickname:
        isSocialLogin && socialUserData ? socialUserData.nickname || "" : "",
      country: "",
      country_name: "",
      postcode: "",
      address: "",
      address_detail: "",
      gender: "",
      year:
        isSocialLogin && socialUserData
          ? socialUserData?.birth_date?.substring(0, 4)
          : "",
      month:
        isSocialLogin && socialUserData
          ? socialUserData?.birth_date?.substring(4, 6)
          : "",
      day:
        isSocialLogin && socialUserData
          ? socialUserData?.birth_date?.substring(6, 8)
          : "",
      isNicknameUnique: false,
      oauth_provider:
        isSocialLogin && socialUserData?.oauth_provider
          ? socialUserData?.oauth_provider
          : "",
      oauth_id:
        isSocialLogin && socialUserData.oauth_id ? socialUserData.oauth_id : "",
    },
  });
  const {
    setValue,
    handleSubmit,
    watch,
    formState: { errors },
  } = methods;

  useEffect(() => {
    if (isSocialLogin && socialUserData?.oauth_provider === "APPLE") {
      const appleLastName = localStorage.getItem("apple_last_name");
      const appleFirstName = localStorage.getItem("apple_first_name");

      if (appleLastName || appleFirstName) {
        const appleName =
          `${appleLastName || ""} ${appleFirstName || ""}`.trim();
        if (appleName) {
          setValue("name", appleName);
        }
      }

      const appleEmail = localStorage.getItem("apple_email");
      if (appleEmail) {
        setValue("login_id", appleEmail.trim());
      }
    }
  }, [isSocialLogin, socialUserData, setValue]);

  // console.log("회원가입errors", errors);

  const onSubmit = async (data: IFormInput) => {
    const postData = {
      login_id: isSocialLogin
        ? socialUserData?.oauth_provider === "APPLE"
          ? data.login_id // 애플 로그인은 form data에서 가져옴
          : socialUserData?.email // 다른 소셜 로그인은 socialUserData에서
        : data.login_id, // 일반 로그인
      password: isSocialLogin ? null : data.password,
      profile_image: null,
      name: data.name,
      nickname: data.nickname,
      gender: data.gender,
      country: data.country,
      postcode: data.postcode,
      address: data.address,
      address_detail: data.address_detail,
      birth_date: `${data.year}${data.month}${data.day}`,
      ...(isSocialLogin && {
        oauth_provider: data.oauth_provider,
        oauth_id: data.oauth_id,
      }),
    };

    const res = await axios.post(SIGN_UP_URL, postData);

    if (res.status === 200) {
      // 소셜 로그인일 경우 step이 한단계 적음
      const successStep = isSocialLogin ? 1 : 2;
      setStep(successStep);
    }
  };

  const handleBackEvent = () => {
    if (isSocialLogin) {
      // 소셜 로그인의 경우 - 1스텝(SignupForm)에서 바로 로그인페이지로
      navigate("/login");
    } else {
      // 일반 로그인의 경우
      if (step === 0) {
        navigate(-1); // 첫 단계면 이전 페이지로
      } else {
        decreaseStep(); // 그 외에는 이전 스텝으로
      }
    }
  };

  const tabList = [
    ...(isSocialLogin ? [] : [{ component: <VerifyPassword /> }]),

    { component: <SignupForm onSubmit={handleSubmit(onSubmit)} /> },
    {
      component: (
        <SuccessMessage
          message="회원가입이 완료되었습니다"
          buttonText="로그인 하기"
          onButtonClick={() => {
            navigate("/login");
          }}
        />
      ),
    },
  ];

  return (
    <>
      {/* 마지막 단계(SuccessMessage)가 아닐 때만 BackHeader */}
      {step !== tabList.length - 1 && (
        <BackHeader title="회원가입" onClick={handleBackEvent} />
      )}
      <FormProvider {...methods}>
        <Container>{tabList[step].component}</Container>
      </FormProvider>
    </>
  );
}

const Container = styled.div(() => ({
  padding: "2rem",
}));
